import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { Breadcrumbs } from '../../components/shared/Breadcrumbs'
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed'

export default function AzureSiteRecoveryPricingGuide() {
  return (
    <>
      <Helmet>
        <title>Azure Site Recovery Pricing UK 2026 — The Full DR Cost Picture | AzureCalc.uk</title>
        <meta
          name="description"
          content="Azure Site Recovery pricing explained for UK teams. The per-instance fee is only part of the bill — replica storage, replication transfer, and failover compute typically make the real cost 3–4× higher. All in GBP."
        />
        <link rel="canonical" href="https://www.azure-calc.co.uk/guides/azure-site-recovery-pricing-uk" />
        <meta property="og:title" content="Azure Site Recovery Pricing UK 2026 — The Full DR Cost Picture" />
        <meta property="og:url" content="https://www.azure-calc.co.uk/guides/azure-site-recovery-pricing-uk" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <Breadcrumbs />

        <h1 className="text-2xl font-bold tracking-tight mt-4 mb-3" style={{ color: 'var(--text-primary)' }}>
          Azure Site Recovery Pricing UK 2026
        </h1>
        <p className="text-[14px] mb-2" style={{ color: 'var(--text-tertiary)' }}>
          Per-instance fees · Replica storage · Replication transfer · Failover compute
        </p>
        <p className="text-[12px] mb-8 italic" style={{ color: 'var(--text-tertiary)' }}>
          Built and verified by an independent Azure engineer.
        </p>

        {/* Section 1: The four components */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            1. The four components of ASR cost — and why only one is on the pricing page
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            The Azure Site Recovery pricing page shows a single per-instance fee. For Azure VMs it looks affordable — £18.87/month per VM. What the pricing page doesn't show is that the per-instance fee is typically the smallest component of your actual DR bill. Once you add replica managed disk storage in the target region, ongoing delta replication transfer between regions, and the compute cost of quarterly failover tests, the total is commonly 3–4× the per-instance fee. The calculator exists to show all four components together — because building a DR business case on the per-instance fee alone will underestimate the real cost significantly.
          </p>

          <div
            className="rounded-lg overflow-hidden text-[13px] mb-4"
            style={{ border: '1px solid var(--outline-variant)' }}
          >
            <table className="w-full">
              <thead style={{ backgroundColor: 'var(--surface-container-low)' }}>
                <tr>
                  <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Component</th>
                  <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>On pricing page?</th>
                  <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Typical share of total</th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: 'var(--surface-container)' }}>
                <tr style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>Per-instance fee</td>
                  <td className="px-4 py-2" style={{ color: '#16a34a' }}>Yes</td>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>20–30%</td>
                </tr>
                <tr style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>Replica managed disk storage</td>
                  <td className="px-4 py-2" style={{ color: '#dc2626' }}>No</td>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>40–60%</td>
                </tr>
                <tr style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>Replication data transfer</td>
                  <td className="px-4 py-2" style={{ color: '#dc2626' }}>No</td>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>5–20%</td>
                </tr>
                <tr style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>Failover test compute</td>
                  <td className="px-4 py-2" style={{ color: '#dc2626' }}>No</td>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>5–15%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            The replica storage component is often the largest single line item — for a VM with a 256 GB disk, replica storage alone at Standard HDD LRS costs £8.32/month versus the £18.87 instance fee. Once you include GRS redundancy or multiple disks, replica storage can exceed the instance fee entirely.
          </p>
        </section>

        {/* Section 2: Per-instance fees */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            2. Per-instance fees — Azure VMs vs on-premises, and the 31-day free trial
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            The per-instance fee is £18.87/month (derived from $25 USD at the Microsoft implied GBP rate of 0.7548). This applies to Azure VMs, on-premises Hyper-V and VMware VMs, and instances of SQL Server and SAP HANA that you want to protect.
          </p>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            Each newly protected instance has a 31-day free trial. This is designed to cover the initial replication window — ASR needs to send the full disk to the target region before delta replication begins. During the trial, you only pay for storage and transfer, not the instance fee. From day 32, the monthly instance fee begins.
          </p>
          <div
            className="rounded p-3 text-[12px] leading-relaxed"
            style={{ backgroundColor: 'var(--surface-container)', borderLeft: '3px solid #f59e0b', color: 'var(--text-secondary)' }}
          >
            <p>
              <span className="font-medium">Trap:</span> The 31-day free trial applies per instance per protection setup. If you remove protection and re-add an instance, the trial resets — but the initial replication transfer cost recurs in full.
            </p>
          </div>
        </section>

        {/* Section 3: Replica storage */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            3. Replica storage — why it's often the largest component
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            ASR maintains a complete replica of each protected disk in the target region. The replica is a Standard HDD managed disk by default — appropriate for DR since it doesn't serve live traffic. The disk is the same size as the source (or larger if the source expands). You pay the managed disk rate continuously, not just during active replication.
          </p>

          <div
            className="rounded-lg overflow-hidden text-[13px] mb-4"
            style={{ border: '1px solid var(--outline-variant)' }}
          >
            <table className="w-full">
              <thead style={{ backgroundColor: 'var(--surface-container-low)' }}>
                <tr>
                  <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Redundancy</th>
                  <th className="text-right px-4 py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Rate</th>
                  <th className="text-right px-4 py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>128 GB example</th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: 'var(--surface-container)' }}>
                <tr style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>LRS (recommended)</td>
                  <td className="text-right px-4 py-2 font-mono" style={{ color: 'var(--text-primary)' }}>£0.0325/GB/month</td>
                  <td className="text-right px-4 py-2 font-mono" style={{ color: 'var(--text-primary)' }}>£4.16/month</td>
                </tr>
                <tr style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>ZRS</td>
                  <td className="text-right px-4 py-2 font-mono" style={{ color: 'var(--text-primary)' }}>£0.0491/GB/month</td>
                  <td className="text-right px-4 py-2 font-mono" style={{ color: 'var(--text-primary)' }}>£6.28/month</td>
                </tr>
                <tr style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>GRS</td>
                  <td className="text-right px-4 py-2 font-mono" style={{ color: 'var(--text-primary)' }}>£0.0649/GB/month</td>
                  <td className="text-right px-4 py-2 font-mono" style={{ color: 'var(--text-primary)' }}>£8.31/month</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            LRS is the typical choice for DR replica disks. The DR region itself provides geographic redundancy — adding GRS on top of that is redundancy of redundancy. Use the <Link to="/managed-disks" className="underline" style={{ color: '#0078d4' }}>Managed Disks calculator</Link> to model different disk sizes and types precisely.
          </p>
        </section>

        {/* Section 4: Replication transfer */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            4. Replication data transfer — initial vs delta
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            ASR transfer happens in two phases. Initial replication sends the complete disk to the target region — for a 256 GB disk, that is a one-off 256 GB transfer at £0.015/GB = £3.84. This is typically the largest single transfer event and occurs once per protection setup.
          </p>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            Delta replication is the ongoing cost — only changed blocks are sent, typically 5–20 GB/month for a busy VM and under 5 GB/month for a quiescent server. At £0.015/GB (UK South → UK West cross-region rate from D1), 10 GB/month of delta replication costs £0.15/month — a minor line item. The initial replication transfer is more significant and worth modelling separately for the onboarding budget.
          </p>
          <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            Use the <Link to="/bandwidth" className="underline" style={{ color: '#0078d4' }}>Bandwidth calculator</Link> for detailed transfer cost modelling including the initial replication event.
          </p>
        </section>

        {/* Section 5: Failover test compute */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            5. Failover test compute — why regular testing is non-negotiable and what it costs
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            DR without testing is not DR — it is hope. Microsoft recommends at least two failover tests per year. For critical workloads with an RTO/RPO SLA, quarterly testing is standard. During a test failover, ASR spins up VMs in the target region and runs them for the duration of the test. These VMs are billed at normal compute rates.
          </p>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            For a 4-hour quarterly test of 5 D2s v3 VMs at £0.073/hr: 4 × 5 × £0.073 = £1.46 per test, £5.84/year, £0.49/month annualised. At this scale, failover compute is the smallest line item. At larger scales — 50 VMs, D8s v3, 8-hour tests — it becomes more material: £233.60/test, £77.87/month.
          </p>
          <div
            className="rounded p-3 text-[12px] leading-relaxed"
            style={{ backgroundColor: 'var(--surface-container)', borderLeft: '3px solid #0078d4', color: 'var(--text-secondary)' }}
          >
            <p>
              <span className="font-medium">Cost control:</span> ASR test failovers spin up VMs in an isolated network and do not affect production. Shut them down promptly after validation — leaving them running doubles the test compute cost for every extra hour.
            </p>
          </div>
        </section>

        {/* Section 6: Azure Hybrid Benefit */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            6. Azure Hybrid Benefit — who qualifies and how much it saves
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            If you are protecting on-premises Windows Server VMs and you have active Software Assurance on your Windows Server licences, Azure Hybrid Benefit applies. It reduces the Windows licensing component of the VM compute cost — approximately 30% of the instance fee for eligible on-premises workloads. SQL Server licences with active SA also qualify for AHB on the SQL Server compute cost.
          </p>
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            Azure VMs that you are protecting within Azure (Azure-to-Azure replication) do not receive AHB on the ASR instance fee — the licensing model is different. AHB applies most clearly to on-premises to Azure DR scenarios.
          </p>
        </section>

        <div className="mb-10">
          <CalculatorEmbed service="site-recovery" />
        </div>

        <div
          className="mt-8 pt-6 text-[13px]"
          style={{ borderTop: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}
        >
          <p>
            Use the{' '}
            <Link to="/site-recovery" className="underline" style={{ color: '#0078d4' }}>
              Azure Site Recovery Calculator →
            </Link>{' '}
            to estimate your costs.
          </p>
        </div>
      </div>
    </>
  )
}
